import { useState, useEffect } from 'react';
import { clients, calls, contacts, opportunities } from '@/lib/api';
import { formatCurrency, formatDate, formatDateTime } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Plus, Users, Phone, Mail, Building, MapPin, 
  MoreVertical, Trash2, PhoneCall, PhoneIncoming, PhoneOutgoing, 
  FileText, Target, UserPlus, CalendarClock, Briefcase, Edit, ArrowLeftRight
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog';

const CLIENT_TYPES = {
  consumidor: { label: 'Consumidor Final', description: 'Factura de Consumo (NCF 02)' },
  credito_fiscal: { label: 'Crédito Fiscal', description: 'Deducible ISR/ITBIS (NCF 01)' },
  gubernamental: { label: 'Gubernamental', description: 'Entidades del Estado (NCF 15)' },
  zona_franca: { label: 'Zona Franca', description: 'Régimen Especial (NCF 14)' },
};

const OPPORTUNITY_STATUS = {
  new: { label: 'Nuevo', color: 'bg-slate-500' },
  qualification: { label: 'Calificación', color: 'bg-blue-500' },
  proposal: { label: 'Propuesta', color: 'bg-violet-500' },
  negotiation: { label: 'Negociación', color: 'bg-amber-500' },
  won: { label: 'Ganada', color: 'bg-emerald-500' },
  lost: { label: 'Perdida', color: 'bg-rose-500' },
};

const VERTICALS = {
  logistics: { label: 'Servicios Logísticos', color: 'text-blue-600' },
  consulting: { label: 'Consultoría', color: 'text-violet-600' },
  technology: { label: 'Tecnología', color: 'text-emerald-600' },
};

// Create Client Dialog - Company focused
function CreateClientDialog({ open, onOpenChange, onSubmit }) {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', address: '', 
    rnc: '', client_type: 'credito_fiscal', department: '', notes: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.client_type !== 'consumidor' && !formData.rnc) {
      toast.error('RNC es requerido para este tipo de empresa');
      return;
    }
    // Use company name as primary name if company is filled
    const submitData = { ...formData };
    if (formData.company && !formData.name) {
      submitData.name = formData.company;
    }
    onSubmit(submitData);
    setFormData({ name: '', email: '', phone: '', company: '', address: '', rnc: '', client_type: 'credito_fiscal', department: '', notes: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Building className="h-5 w-5" />
            Nueva Empresa
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company">Nombre de la Empresa *</Label>
              <Input id="company" placeholder="Ej: Transtar SRL" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value, name: e.target.value })} required data-testid="client-company-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Contacto Principal</Label>
              <Input id="name" placeholder="Nombre del contacto" value={formData.name !== formData.company ? formData.name : ''} onChange={(e) => setFormData({ ...formData, name: e.target.value || formData.company })} data-testid="client-name-input" />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tipo Fiscal (NCF) *</Label>
              <Select value={formData.client_type} onValueChange={(value) => setFormData({ ...formData, client_type: value })}>
                <SelectTrigger data-testid="client-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CLIENT_TYPES).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{CLIENT_TYPES[formData.client_type]?.description}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rnc">RNC {formData.client_type !== 'consumidor' && '*'}</Label>
              <Input id="rnc" placeholder="000-00000-0" value={formData.rnc} onChange={(e) => setFormData({ ...formData, rnc: e.target.value })} required={formData.client_type !== 'consumidor'} data-testid="client-rnc-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Corporativo</Label>
              <Input id="email" type="email" placeholder="info@empresa.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="client-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Teléfono</Label>
              <Input id="phone" placeholder="809-000-0000" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="client-phone-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="department">Sector/Industria</Label>
              <Input id="department" placeholder="Ej: Logística, Manufactura" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} data-testid="client-department-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Dirección</Label>
              <Input id="address" placeholder="Dirección de la empresa" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} data-testid="client-address-input" />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes">Notas</Label>
            <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} data-testid="client-notes-input" />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" data-testid="client-submit-btn">Agregar Empresa</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Create Contact Dialog
function CreateContactDialog({ open, onOpenChange, onSubmit, clientsList }) {
  const [formData, setFormData] = useState({
    client_id: '', name: '', position: '', email: '', phone: '', is_primary: false, notes: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.client_id || !formData.name || !formData.position) {
      toast.error('Empresa, nombre y cargo son requeridos');
      return;
    }
    onSubmit(formData);
    setFormData({ client_id: '', name: '', position: '', email: '', phone: '', is_primary: false, notes: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Nuevo Contacto
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Empresa *</Label>
            <Select value={formData.client_id} onValueChange={(value) => setFormData({ ...formData, client_id: value })}>
              <SelectTrigger data-testid="contact-client-select"><SelectValue placeholder="Seleccionar empresa" /></SelectTrigger>
              <SelectContent>
                {clientsList.map((client) => (
                  <SelectItem key={client.client_id} value={client.client_id}>{client.company || client.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact-name">Nombre *</Label>
              <Input id="contact-name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="contact-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-position">Cargo/Posición *</Label>
              <Input id="contact-position" placeholder="Ej: Gerente de Compras" value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} required data-testid="contact-position-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact-email">Email</Label>
              <Input id="contact-email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="contact-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-phone">Teléfono</Label>
              <Input id="contact-phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="contact-phone-input" />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" id="is-primary" checked={formData.is_primary} onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })} className="h-4 w-4" />
            <Label htmlFor="is-primary" className="text-sm">Contacto principal</Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-notes">Notas</Label>
            <Textarea id="contact-notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" data-testid="contact-submit-btn">Crear Contacto</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Create Opportunity Dialog
function CreateOpportunityDialog({ open, onOpenChange, onSubmit, clientsList, contactsList }) {
  const [formData, setFormData] = useState({
    client_id: '', name: '', description: '', value: '', vertical: 'logistics', 
    status: 'new', expected_close_date: '', contact_id: '',
  });

  const filteredContacts = contactsList.filter(c => c.client_id === formData.client_id);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.client_id || !formData.name) {
      toast.error('Cliente y nombre son requeridos');
      return;
    }
    onSubmit({
      ...formData,
      value: parseFloat(formData.value) || 0,
      contact_id: formData.contact_id || undefined,
    });
    setFormData({ client_id: '', name: '', description: '', value: '', vertical: 'logistics', status: 'new', expected_close_date: '', contact_id: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Target className="h-5 w-5" />
            Nuevo Negocio
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Cliente *</Label>
            <Select value={formData.client_id} onValueChange={(value) => setFormData({ ...formData, client_id: value, contact_id: '' })}>
              <SelectTrigger data-testid="opp-client-select"><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
              <SelectContent>
                {clientsList.map((client) => (
                  <SelectItem key={client.client_id} value={client.client_id}>{client.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="opp-name">Nombre del Negocio *</Label>
            <Input id="opp-name" placeholder="Ej: Proyecto de Importación Q1" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="opp-name-input" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Vertical</Label>
              <Select value={formData.vertical} onValueChange={(value) => setFormData({ ...formData, vertical: value })}>
                <SelectTrigger data-testid="opp-vertical-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(VERTICALS).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="opp-value">Valor Estimado (RD$)</Label>
              <Input id="opp-value" type="number" min="0" step="0.01" value={formData.value} onChange={(e) => setFormData({ ...formData, value: e.target.value })} data-testid="opp-value-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger data-testid="opp-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(OPPORTUNITY_STATUS).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="opp-close-date">Fecha Cierre Esperada</Label>
              <Input id="opp-close-date" type="date" value={formData.expected_close_date} onChange={(e) => setFormData({ ...formData, expected_close_date: e.target.value })} data-testid="opp-close-date-input" />
            </div>
          </div>

          {formData.client_id && filteredContacts.length > 0 && (
            <div className="space-y-2">
              <Label>Contacto Principal</Label>
              <Select value={formData.contact_id} onValueChange={(value) => setFormData({ ...formData, contact_id: value })}>
                <SelectTrigger><SelectValue placeholder="Seleccionar contacto (opcional)" /></SelectTrigger>
                <SelectContent>
                  {filteredContacts.map((contact) => (
                    <SelectItem key={contact.contact_id} value={contact.contact_id}>
                      {contact.name} {contact.position && `(${contact.position})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="opp-description">Descripción</Label>
            <Textarea id="opp-description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} data-testid="opp-description-input" />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" data-testid="opp-submit-btn">Crear Negocio</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Log Call Dialog
function LogCallDialog({ open, onOpenChange, onSubmit, clientsList }) {
  const [formData, setFormData] = useState({
    client_id: '', contact_name: '', subject: '', call_datetime: '', duration_minutes: '', call_type: 'outbound', description: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.client_id || !formData.contact_name || !formData.subject || !formData.call_datetime) {
      toast.error('Empresa, persona, tema y fecha/hora son requeridos');
      return;
    }
    onSubmit({ ...formData, duration_minutes: parseInt(formData.duration_minutes) || 0 });
    setFormData({ client_id: '', contact_name: '', subject: '', call_datetime: '', duration_minutes: '', call_type: 'outbound', description: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Registrar Llamada</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Empresa *</Label>
              <Select value={formData.client_id} onValueChange={(value) => setFormData({ ...formData, client_id: value })}>
                <SelectTrigger data-testid="call-client-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  {clientsList.map((client) => (
                    <SelectItem key={client.client_id} value={client.client_id}>{client.company || client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Persona contactada *</Label>
              <Input placeholder="Nombre del contacto" value={formData.contact_name} onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })} required />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Tema *</Label>
            <Input placeholder="Ej: Seguimiento cotizacion" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} required />
          </div>
          <div className="space-y-1.5">
            <Label>Fecha y hora *</Label>
            <Input type="datetime-local" value={formData.call_datetime} onChange={(e) => setFormData({ ...formData, call_datetime: e.target.value })} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={formData.call_type} onValueChange={(value) => setFormData({ ...formData, call_type: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="outbound">Saliente</SelectItem>
                  <SelectItem value="inbound">Entrante</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Duracion (min)</Label>
              <Input type="number" min="0" value={formData.duration_minutes} onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Descripcion (opcional)</Label>
            <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" data-testid="call-submit-btn">Registrar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CRM() {
  const { user } = useAuth();
  const [clientsList, setClientsList] = useState([]);
  const [callsList, setCallsList] = useState([]);
  const [contactsList, setContactsList] = useState([]);
  const [opportunitiesList, setOpportunitiesList] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [opportunityDialogOpen, setOpportunityDialogOpen] = useState(false);
  
  // Delete dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState({ type: null, id: null, name: null });

  const isAdmin = user?.role === 'admin';

  // Show/hide demo examples
  const [showDemo, setShowDemo] = useState(true);

  // Demo data state - editable examples (values always stored in RD$)
  const [demoOpps, setDemoOpps] = useState([
    { id: 'd1', name: 'Embarque Guatemala', company: 'Grupo Magalys', contact: 'Magalys Rodriguez', vertical: 'logistics', status: 'new', value: 120000, currency: 'DOP', close: '2026-04-15' },
    { id: 'd2', name: 'Contenedores Larissa', company: 'Larissa Corp', contact: 'Larissa Mendez', vertical: 'logistics', status: 'new', value: 65000, currency: 'DOP', close: '2026-04-20' },
    { id: 'd3', name: 'Servicio GPS Geotab', company: 'Bravo Logistics', contact: 'Carlos Bravo', vertical: 'technology', status: 'qualification', value: 250000, currency: 'DOP', close: '2026-05-01' },
    { id: 'd4', name: 'Proyecto CMA-CGM', company: 'CMA-CGM RD', contact: 'Cynthia Polanco', vertical: 'logistics', status: 'proposal', value: 189909, currency: 'DOP', close: '2026-05-15' },
    { id: 'd5', name: 'Grydd Lighthouse', company: 'Grydd Tech', contact: 'Kelvin Mendez', vertical: 'technology', status: 'negotiation', value: 350000, currency: 'DOP', close: '2026-06-01' },
    { id: 'd6', name: 'Sim Cards CAT', company: 'CAT Telecom', contact: 'Fausto Garcia', vertical: 'technology', status: 'won', value: 75000, currency: 'DOP', close: '2026-03-01' },
    { id: 'd7', name: 'Embarque Mexico', company: 'Polanco Import', contact: 'Ana Polanco', vertical: 'logistics', status: 'lost', value: 45000, currency: 'DOP', close: '2026-02-15' },
  ]);

  // Exchange rate
  const [exchangeRate, setExchangeRate] = useState(60.5); // USD to DOP
  const [rateUpdatedAt, setRateUpdatedAt] = useState('');

  const updateDemoOpp = (id, field, val) => {
    setDemoOpps(prev => prev.map(o => o.id === id ? { ...o, [field]: field === 'value' ? (parseFloat(val) || 0) : val } : o));
  };

  const toggleDemoCurrency = (id) => {
    setDemoOpps(prev => prev.map(o => {
      if (o.id !== id) return o;
      if (o.currency === 'DOP') {
        return { ...o, currency: 'USD', value: Math.round((o.value / exchangeRate) * 100) / 100 };
      } else {
        return { ...o, currency: 'DOP', value: Math.round(o.value * exchangeRate) };
      }
    }));
  };

  const toggleAllDemoCurrency = (toCurrency) => {
    setDemoOpps(prev => prev.map(o => {
      if (o.currency === toCurrency) return o;
      if (toCurrency === 'USD') {
        return { ...o, currency: 'USD', value: Math.round((o.value / exchangeRate) * 100) / 100 };
      } else {
        return { ...o, currency: 'DOP', value: Math.round(o.value * exchangeRate) };
      }
    }));
  };

  const formatDemoValue = (opp) => {
    const symbol = opp.currency === 'USD' ? 'US$' : 'RD$';
    return `${symbol}${opp.value.toLocaleString('en-US', { minimumFractionDigits: opp.currency === 'USD' ? 2 : 0 })}`;
  };

  const demoPipelineValue = (opps) => {
    // Convert all to DOP for consistent totals
    return opps.reduce((s, o) => s + (o.currency === 'USD' ? o.value * exchangeRate : o.value), 0);
  };

  const demoCompanies = [...new Map(demoOpps.map(o => [o.company, o])).values()].map(o => ({
    company: o.company, contact: o.contact, vertical: o.vertical
  }));

  useEffect(() => {
    loadData();
    loadExchangeRate();
  }, []);

  const loadExchangeRate = async () => {
    try {
      const res = await clients.getExchangeRate();
      setExchangeRate(res.data.rate);
      setRateUpdatedAt(res.data.updated_at);
    } catch (e) {}
  };

  const loadData = async () => {
    try {
      const [clientsRes, callsRes, contactsRes, oppsRes] = await Promise.all([
        clients.getAll(),
        calls.getAll(),
        contacts.getAll(),
        opportunities.getAll(),
      ]);
      setClientsList(clientsRes.data);
      setCallsList(callsRes.data);
      setContactsList(contactsRes.data);
      setOpportunitiesList(oppsRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestDelete = (type, id, name) => {
    setDeleteItem({ type, id, name });
    setDeleteDialogOpen(true);
  };

  const handleCreateClient = async (data) => {
    try {
      await clients.create(data);
      toast.success('Empresa creada exitosamente');
      setClientDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error('Error al crear empresa');
    }
  };

  const handleLogCall = async (data) => {
    try {
      await calls.create(data);
      toast.success('Llamada registrada');
      setCallDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error('Error al registrar llamada');
    }
  };

  const handleCreateContact = async (data) => {
    try {
      await contacts.create(data);
      toast.success('Contacto creado exitosamente');
      setContactDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error('Error al crear contacto');
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await contacts.delete(contactId);
      toast.success('Contacto eliminado');
      loadData();
    } catch (err) {
      toast.error('Error al eliminar contacto');
    }
  };

  const handleCreateOpportunity = async (data) => {
    try {
      await opportunities.create(data);
      toast.success('Negocio creado exitosamente');
      setOpportunityDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error('Error al crear negocio');
    }
  };

  const handleDeleteOpportunity = async (oppId) => {
    try {
      await opportunities.delete(oppId);
      toast.success('Negocio eliminado');
      loadData();
    } catch (err) {
      toast.error('Error al eliminar negocio');
    }
  };

  const handleUpdateOpportunityStatus = async (oppId, newStatus) => {
    try {
      await opportunities.update(oppId, { status: newStatus });
      toast.success('Estado actualizado');
      loadData();
    } catch (err) {
      toast.error('Error al actualizar estado');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Calculate stats
  const totalPipeline = opportunitiesList.filter(o => !['won', 'lost'].includes(o.status)).reduce((sum, o) => sum + o.value, 0);
  const wonOpps = opportunitiesList.filter(o => o.status === 'won');

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="crm-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight">CRM</h1>
          <p className="text-muted-foreground">Gestión de empresas, contactos y negocios</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCallDialogOpen(true)} data-testid="log-call-btn">
            <Phone className="h-4 w-4 mr-2" />
            Llamada
          </Button>
          <Button variant="outline" onClick={() => setContactDialogOpen(true)} data-testid="create-contact-btn">
            <UserPlus className="h-4 w-4 mr-2" />
            Contacto
          </Button>
          <Button variant="outline" onClick={() => setOpportunityDialogOpen(true)} data-testid="create-opp-btn">
            <Target className="h-4 w-4 mr-2" />
            Negocio
          </Button>
          <Button variant={showDemo ? "secondary" : "outline"} size="sm" onClick={() => setShowDemo(!showDemo)} data-testid="toggle-demo-btn" className="ml-2">
            {showDemo ? 'Ocultar Ejemplos' : 'Ver Ejemplos'}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide font-heading">Empresas</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-heading">{clientsList.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide font-heading">Contactos</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-heading">{contactsList.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide font-heading">Negocios</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-heading">{opportunitiesList.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide font-heading">Pipeline</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-heading text-blue-500">{formatCurrency(totalPipeline)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide font-heading">Ganadas</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-heading text-emerald-500">{formatCurrency(wonOpps.reduce((s, o) => s + o.value, 0))}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="pipeline" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pipeline" className="gap-2" data-testid="pipeline-tab"><Target className="h-4 w-4" />Pipeline</TabsTrigger>
          <TabsTrigger value="contacts" className="gap-2"><UserPlus className="h-4 w-4" />Contactos</TabsTrigger>
          <TabsTrigger value="opportunities" className="gap-2"><Target className="h-4 w-4" />Negocios</TabsTrigger>
          <TabsTrigger value="calls" className="gap-2"><Phone className="h-4 w-4" />Llamadas</TabsTrigger>
        </TabsList>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline" data-testid="pipeline-content">
          {/* Add opportunity button */}
          <div className="flex justify-end mb-4">
            <Button onClick={() => setOpportunityDialogOpen(true)} size="sm" data-testid="pipeline-add-opp-btn">
              <Plus className="h-4 w-4 mr-2" />
              Agregar Negocio
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(OPPORTUNITY_STATUS).map(([stageKey, { label, color }]) => {
              const stageOpps = opportunitiesList.filter(o => o.status === stageKey);
              const stageTotal = stageOpps.reduce((sum, o) => sum + (o.value || 0), 0);
              
              return (
                <div key={stageKey} className="space-y-3" data-testid={`pipeline-stage-${stageKey}`}>
                  {/* Stage header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
                      <span className="text-sm font-semibold">{label}</span>
                    </div>
                    <Badge variant="outline" className="text-xs">{stageOpps.length}</Badge>
                  </div>
                  <div className="text-xs font-mono text-muted-foreground">{formatCurrency(stageTotal)}</div>
                  
                  {/* Opportunity cards */}
                  <div className="space-y-2 min-h-[100px]">
                    {stageOpps.length === 0 ? (
                      <div className="border border-dashed rounded-lg p-4 text-center text-xs text-muted-foreground">
                        Sin negocios
                      </div>
                    ) : (
                      stageOpps.map(opp => {
                        const client = clientsList.find(c => c.client_id === opp.client_id);
                        return (
                          <Card key={opp.opportunity_id} className="hover:shadow-md transition-shadow cursor-default" data-testid={`pipeline-card-${opp.opportunity_id}`}>
                            <CardContent className="p-3 space-y-2">
                              <p className="text-sm font-semibold leading-tight">{opp.name}</p>
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Building className="h-3 w-3" />
                                <span>{client?.company || opp.client_name || 'Sin empresa'}</span>
                              </div>
                              {opp.contact_name && (
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                  <UserPlus className="h-3 w-3" />
                                  <span>{opp.contact_name}</span>
                                </div>
                              )}
                              <div className="flex items-center justify-between pt-1">
                                <span className={`text-xs font-medium ${VERTICALS[opp.vertical]?.color || ''}`}>
                                  {VERTICALS[opp.vertical]?.label || ''}
                                </span>
                                <span className="text-sm font-bold font-mono">{formatCurrency(opp.value)}</span>
                              </div>
                              {opp.expected_close_date && (
                                <div className="text-xs text-muted-foreground flex items-center gap-1">
                                  <CalendarClock className="h-3 w-3" />
                                  {formatDate(opp.expected_close_date)}
                                </div>
                              )}
                              {/* Quick status change */}
                              <Select value={opp.status} onValueChange={(value) => handleUpdateOpportunityStatus(opp.opportunity_id, value)}>
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.entries(OPPORTUNITY_STATUS).map(([key, { label: l, color: c }]) => (
                                    <SelectItem key={key} value={key}>
                                      <div className="flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full ${c}`} />
                                        {l}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </CardContent>
                          </Card>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pipeline by Company summary */}
          {clientsList.length > 0 && opportunitiesList.length > 0 && (
            <Card className="mt-6">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building className="h-5 w-5 text-blue-500" />
                  Pipeline por Empresa
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {clientsList.map(client => {
                    const clientOpps = opportunitiesList.filter(o => o.client_id === client.client_id);
                    if (clientOpps.length === 0) return null;
                    const clientTotal = clientOpps.reduce((s, o) => s + (o.value || 0), 0);
                    const activeTotal = clientOpps.filter(o => !['won', 'lost'].includes(o.status)).reduce((s, o) => s + (o.value || 0), 0);
                    const wonTotal = clientOpps.filter(o => o.status === 'won').reduce((s, o) => s + (o.value || 0), 0);
                    const maxTotal = Math.max(...clientsList.map(c => opportunitiesList.filter(o => o.client_id === c.client_id).reduce((s, o) => s + (o.value || 0), 0)), 1);

                    return (
                      <div key={client.client_id} className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Building className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium text-sm">{client.company || client.name}</span>
                            <Badge variant="outline" className="text-xs">{clientOpps.length} oport.</Badge>
                          </div>
                          <div className="flex items-center gap-3 text-sm font-mono">
                            {wonTotal > 0 && <span className="text-emerald-500">{formatCurrency(wonTotal)} ganado</span>}
                            <span className="text-blue-500 font-semibold">{formatCurrency(activeTotal)} activo</span>
                          </div>
                        </div>
                        {/* Stage breakdown bar */}
                        <div className="h-3 bg-muted rounded-full overflow-hidden flex" style={{ width: `${Math.max((clientTotal / maxTotal) * 100, 15)}%` }}>
                          {Object.entries(OPPORTUNITY_STATUS).map(([key, { color }]) => {
                            const stageVal = clientOpps.filter(o => o.status === key).reduce((s, o) => s + (o.value || 0), 0);
                            if (stageVal === 0) return null;
                            return <div key={key} className={`${color} transition-all`} style={{ width: `${(stageVal / clientTotal) * 100}%` }} />;
                          })}
                        </div>
                        {/* Stage badges */}
                        <div className="flex gap-1.5 flex-wrap">
                          {Object.entries(OPPORTUNITY_STATUS).map(([key, { label: l, color: c }]) => {
                            const count = clientOpps.filter(o => o.status === key).length;
                            if (count === 0) return null;
                            return (
                              <Badge key={key} variant="outline" className="text-xs gap-1">
                                <div className={`w-1.5 h-1.5 rounded-full ${c}`} />
                                {count} {l}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Example Pipeline - Transtar HU Demo */}
          {showDemo && (
          <Card className="mt-6 border-2 border-dashed border-blue-300 bg-blue-50/30" data-testid="pipeline-example">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2 text-blue-700">
                  <Briefcase className="h-5 w-5" />
                  Ejemplo: Pipeline de Transtar HU
                </CardTitle>
                <Badge className="bg-blue-100 text-blue-700 border-blue-300">EJEMPLO DEMOSTRATIVO</Badge>
              </div>
              <p className="text-xs text-blue-600 mt-1">
                Los cambios se hacen en la pestana "Negocios". Este pipeline refleja esos datos automaticamente.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                {Object.entries(OPPORTUNITY_STATUS).map(([stageKey, { label: stageLabel, color }]) => {
                  const stageDemo = demoOpps.filter(o => o.status === stageKey);
                  const stageTotal = stageDemo.reduce((s, o) => s + o.value, 0);
                  return (
                    <div key={stageKey} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
                        <span className="text-sm font-semibold">{stageLabel}</span>
                        <Badge variant="outline" className="text-xs">{stageDemo.length}</Badge>
                      </div>
                      <div className="text-xs font-mono text-muted-foreground">
                        {(() => {
                          const allUSD = stageDemo.length > 0 && stageDemo.every(o => o.currency === 'USD');
                          const sum = stageDemo.reduce((s, o) => s + o.value, 0);
                          const curr = allUSD ? 'US$' : 'RD$';
                          return `${curr}${sum.toLocaleString('en-US', { minimumFractionDigits: allUSD ? 2 : 0 })}`;
                        })()}
                      </div>
                      {stageDemo.length === 0 ? (
                        <div className="border border-dashed border-blue-200 rounded-lg p-3 text-center text-xs text-blue-400">Vacio</div>
                      ) : stageDemo.map(opp => (
                        <Card key={opp.id} className="border-blue-200 bg-white">
                          <CardContent className="p-2.5 space-y-1.5">
                            <p className="text-xs font-semibold">{opp.name}</p>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Building className="h-3 w-3" />{opp.company}
                            </div>
                            <div className="flex items-center justify-between">
                              <span className={`text-xs font-medium ${VERTICALS[opp.vertical]?.color || ''}`}>{VERTICALS[opp.vertical]?.label || ''}</span>
                              <span className="text-xs font-mono font-bold">{formatDemoValue(opp)}</span>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* Demo totals summary */}
              {(() => {
                const allUSD = demoOpps.every(o => o.currency === 'USD');
                const curr = allUSD ? 'US$' : 'RD$';
                const sumVal = (list) => list.reduce((s, o) => s + o.value, 0);
                const fmt = (val) => `${curr}${val.toLocaleString('en-US', { minimumFractionDigits: allUSD ? 2 : 0 })}`;
                const activeOpps = demoOpps.filter(o => !['won','lost'].includes(o.status));
                const wonOppsDemo = demoOpps.filter(o => o.status === 'won');
                const lostOpps = demoOpps.filter(o => o.status === 'lost');
                return (
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    <div className="bg-white rounded-lg border border-blue-200 p-3 text-center">
                      <p className="text-xs text-muted-foreground">Pipeline Activo</p>
                      <p className="text-lg font-bold font-mono text-blue-600">{fmt(sumVal(activeOpps))}</p>
                    </div>
                    <div className="bg-white rounded-lg border border-emerald-200 p-3 text-center">
                      <p className="text-xs text-muted-foreground">Ganadas</p>
                      <p className="text-lg font-bold font-mono text-emerald-600">{fmt(sumVal(wonOppsDemo))}</p>
                    </div>
                    <div className="bg-white rounded-lg border border-rose-200 p-3 text-center">
                      <p className="text-xs text-muted-foreground">Perdidas</p>
                      <p className="text-lg font-bold font-mono text-rose-600">{fmt(sumVal(lostOpps))}</p>
                    </div>
                  </div>
                );
              })()}

              {/* How it works explanation */}
              <div className="bg-white rounded-lg border border-blue-200 p-5 space-y-4">
                <h4 className="font-semibold text-sm text-blue-800 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Como funciona el Pipeline
                </h4>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="font-semibold text-xs text-slate-800 mb-3">De donde vienen los datos:</p>
                    <ol className="space-y-2.5 text-xs text-slate-600">
                      <li className="flex gap-2">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs">1</span>
                        <span>Agrega una <strong>Empresa</strong> en la pestana "Empresas"</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs">2</span>
                        <span>Crea una <strong>Negocio</strong> en la pestana "Negocios" asociada a esa empresa</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs">3</span>
                        <span>Asigna un <strong>valor</strong> (RD$), <strong>vertical</strong> y <strong>etapa</strong></span>
                      </li>
                      <li className="flex gap-2">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs">4</span>
                        <span>El negocio aparece automaticamente aqui en el Pipeline</span>
                      </li>
                    </ol>
                  </div>
                  <div>
                    <p className="font-semibold text-xs text-slate-800 mb-3">Prueba con los ejemplos:</p>
                    <div className="space-y-2.5 text-xs text-slate-600">
                      <div className="flex gap-2 items-start">
                        <Badge variant="outline" className="flex-shrink-0 text-xs font-mono">Editar</Badge>
                        <span>Ve a la pestana <strong>Oportunidades</strong> y cambia los montos o etapas en la seccion de ejemplo</span>
                      </div>
                      <div className="flex gap-2 items-start">
                        <Badge variant="outline" className="flex-shrink-0 text-xs font-mono">Pipeline</Badge>
                        <span>Regresa aqui y ve como las tarjetas y totales se actualizaron automaticamente</span>
                      </div>
                      <div className="flex gap-2 items-start">
                        <Badge className="flex-shrink-0 text-xs font-mono bg-emerald-100 text-emerald-700 border-emerald-300">Totales</Badge>
                        <span>Pipeline Activo, Ganadas y Perdidas se recalculan con cada cambio</span>
                      </div>
                      <div className="flex gap-2 items-start">
                        <Badge variant="outline" className="flex-shrink-0 text-xs font-mono">Secciones</Badge>
                        <span>Empresas y Contactos tambien reflejan los datos de ejemplo</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          )}
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts">
          <div className="flex justify-end mb-3">
            <Button size="sm" onClick={() => setContactDialogOpen(true)}><Plus className="h-4 w-4 mr-1" />Agregar Contacto</Button>
          </div>
          <Card>
            <CardContent className="p-0">
              {contactsList.length === 0 ? (
                <p className="text-center text-muted-foreground py-12">No hay contactos registrados</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="uppercase text-xs">Contacto</TableHead>
                      <TableHead className="uppercase text-xs">Cargo</TableHead>
                      <TableHead className="uppercase text-xs">Cliente</TableHead>
                      <TableHead className="uppercase text-xs">Email</TableHead>
                      <TableHead className="uppercase text-xs">Teléfono</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contactsList.map((contact) => {
                      const client = clientsList.find(c => c.client_id === contact.client_id);
                      return (
                        <TableRow key={contact.contact_id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{contact.name}</span>
                              {contact.is_primary && <Badge variant="default" className="text-xs">Principal</Badge>}
                            </div>
                          </TableCell>
                          <TableCell>
                            {contact.position && (
                              <div className="flex items-center gap-1 text-sm">
                                <Briefcase className="h-3 w-3 text-muted-foreground" />{contact.position}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>{client?.name || 'N/A'}</TableCell>
                          <TableCell>{contact.email && <span className="flex items-center gap-1 text-sm"><Mail className="h-3 w-3" />{contact.email}</span>}</TableCell>
                          <TableCell>{contact.phone && <span className="flex items-center gap-1 text-sm"><Phone className="h-3 w-3" />{contact.phone}</span>}</TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="icon" variant="ghost" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem className="text-destructive" onClick={() => handleRequestDelete('contact', contact.contact_id, contact.name)}>
                                  <Trash2 className="h-4 w-4 mr-2" />Eliminar
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Demo Contacts */}
          {showDemo && (
          <Card className="mt-4 border-2 border-dashed border-blue-300 bg-blue-50/30">
            <CardHeader className="py-3 pb-1">
              <div className="flex items-center gap-2">
                <CardTitle className="text-sm text-blue-700">Contactos de Ejemplo</CardTitle>
                <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs">EJEMPLO</Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="uppercase text-xs">Contacto</TableHead>
                    <TableHead className="uppercase text-xs">Cargo</TableHead>
                    <TableHead className="uppercase text-xs">Empresa</TableHead>
                    <TableHead className="uppercase text-xs">Negocio</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {demoOpps.map(o => (
                    <TableRow key={o.id}>
                      <TableCell className="font-medium">{o.contact}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">Contacto Principal</TableCell>
                      <TableCell><div className="flex items-center gap-1"><Building className="h-3 w-3 text-blue-400" />{o.company}</div></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${OPPORTUNITY_STATUS[o.status]?.color}`} />
                          <span className="text-sm">{o.name}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          )}
        </TabsContent>

        {/* Opportunities Tab */}
        <TabsContent value="opportunities">
          <div className="flex justify-end mb-3">
            <Button size="sm" onClick={() => setOpportunityDialogOpen(true)}><Plus className="h-4 w-4 mr-1" />Agregar Negocio</Button>
          </div>
          <Card>
            <CardContent className="p-0">
              {opportunitiesList.length === 0 ? (
                <p className="text-center text-muted-foreground py-12">No hay negocios registrados</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="uppercase text-xs">Número</TableHead>
                      <TableHead className="uppercase text-xs">Negocio</TableHead>
                      <TableHead className="uppercase text-xs">Cliente</TableHead>
                      <TableHead className="uppercase text-xs">Vertical</TableHead>
                      <TableHead className="uppercase text-xs">Estado</TableHead>
                      <TableHead className="uppercase text-xs">Cotizado</TableHead>
                      <TableHead className="uppercase text-xs text-right">Valor</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {opportunitiesList.map((opp) => (
                      <TableRow key={opp.opportunity_id}>
                        <TableCell>
                          <Badge variant="outline" className="font-mono">{opp.opportunity_number}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{opp.name}</div>
                          {opp.contact_name && <div className="text-xs text-muted-foreground">Contacto: {opp.contact_name}</div>}
                        </TableCell>
                        <TableCell>{opp.client_name}</TableCell>
                        <TableCell>
                          <span className={`text-sm font-medium ${VERTICALS[opp.vertical]?.color || ''}`}>
                            {VERTICALS[opp.vertical]?.label || opp.vertical}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Select value={opp.status} onValueChange={(value) => handleUpdateOpportunityStatus(opp.opportunity_id, value)}>
                            <SelectTrigger className="w-[130px] h-8">
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${OPPORTUNITY_STATUS[opp.status]?.color || ''}`} />
                                <span className="text-xs">{OPPORTUNITY_STATUS[opp.status]?.label || opp.status}</span>
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(OPPORTUNITY_STATUS).map(([key, { label, color }]) => (
                                <SelectItem key={key} value={key}>
                                  <div className="flex items-center gap-2">
                                    <div className={`w-2 h-2 rounded-full ${color}`} />
                                    {label}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          {opp.quoted ? (
                            <Badge variant="default" className="gap-1 bg-emerald-500">
                              <FileText className="h-3 w-3" />Sí
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">No</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono font-medium">{formatCurrency(opp.value)}</TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem className="text-destructive" onClick={() => handleRequestDelete('opportunity', opp.opportunity_id, opp.name)}>
                                <Trash2 className="h-4 w-4 mr-2" />Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Demo Opportunities */}
          {showDemo && (
          <Card className="mt-4 border-2 border-dashed border-blue-300 bg-blue-50/30">
            <CardHeader className="py-3 pb-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-sm text-blue-700">Oportunidades de Ejemplo</CardTitle>
                  <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs">EJEMPLO EDITABLE</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Tasa: 1 USD = {exchangeRate.toFixed(2)} DOP</span>
                  <Button variant="outline" size="sm" className="h-7 text-xs gap-1 border-blue-200" onClick={() => toggleAllDemoCurrency(demoOpps[0]?.currency === 'DOP' ? 'USD' : 'DOP')}>
                    <ArrowLeftRight className="h-3 w-3" />
                    Cambiar todo a {demoOpps[0]?.currency === 'DOP' ? 'USD$' : 'RD$'}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-blue-500 mt-1">Cambia valores, etapas y moneda para ver el efecto en el Pipeline</p>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="uppercase text-xs">Negocio</TableHead>
                    <TableHead className="uppercase text-xs">Empresa</TableHead>
                    <TableHead className="uppercase text-xs">Vertical</TableHead>
                    <TableHead className="uppercase text-xs">Etapa</TableHead>
                    <TableHead className="uppercase text-xs text-center">Moneda</TableHead>
                    <TableHead className="uppercase text-xs text-right">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {demoOpps.map(o => (
                    <TableRow key={o.id}>
                      <TableCell>
                        <div className="font-medium">{o.name}</div>
                        <div className="text-xs text-muted-foreground">{o.contact}</div>
                      </TableCell>
                      <TableCell><div className="flex items-center gap-1"><Building className="h-3 w-3 text-blue-400" />{o.company}</div></TableCell>
                      <TableCell>
                        <span className={`text-sm font-medium ${VERTICALS[o.vertical]?.color || ''}`}>{VERTICALS[o.vertical]?.label || ''}</span>
                      </TableCell>
                      <TableCell>
                        <Select value={o.status} onValueChange={(v) => updateDemoOpp(o.id, 'status', v)}>
                          <SelectTrigger className="w-[130px] h-8 border-blue-200">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${OPPORTUNITY_STATUS[o.status]?.color}`} />
                              <span className="text-xs">{OPPORTUNITY_STATUS[o.status]?.label}</span>
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(OPPORTUNITY_STATUS).map(([k, { label: l, color: c }]) => (
                              <SelectItem key={k} value={k}><div className="flex items-center gap-2"><div className={`w-2 h-2 rounded-full ${c}`} />{l}</div></SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 font-mono" onClick={() => toggleDemoCurrency(o.id)}>
                          <ArrowLeftRight className="h-3 w-3" />
                          {o.currency === 'DOP' ? 'RD$' : 'US$'}
                        </Button>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <span className="text-xs text-muted-foreground">{o.currency === 'DOP' ? 'RD$' : 'US$'}</span>
                          <Input
                            type="number"
                            value={o.value}
                            onChange={(e) => updateDemoOpp(o.id, 'value', e.target.value)}
                            className="h-8 w-28 text-right font-mono font-bold text-sm border-blue-200"
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          )}
        </TabsContent>

        {/* Calls Tab */}
        <TabsContent value="calls">
          <div className="flex justify-end mb-3">
            <Button size="sm" onClick={() => setCallDialogOpen(true)}><Plus className="h-4 w-4 mr-1" />Registrar Llamada</Button>
          </div>
          <Card>
            <CardContent className="p-0">
              {callsList.length === 0 ? (
                <p className="text-center text-muted-foreground py-12">No hay llamadas registradas</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="uppercase text-xs">Tipo</TableHead>
                      <TableHead className="uppercase text-xs">Cliente</TableHead>
                      <TableHead className="uppercase text-xs">Usuario</TableHead>
                      <TableHead className="uppercase text-xs">Fecha</TableHead>
                      <TableHead className="uppercase text-xs">Duración</TableHead>
                      <TableHead className="uppercase text-xs">Notas</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {callsList.map((call) => (
                      <TableRow key={call.call_id}>
                        <TableCell>
                          {call.call_type === 'inbound' ? (
                            <Badge variant="outline" className="badge-success gap-1"><PhoneIncoming className="h-3 w-3" />Entrante</Badge>
                          ) : (
                            <Badge variant="outline" className="badge-warning gap-1"><PhoneOutgoing className="h-3 w-3" />Saliente</Badge>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{call.client_name}</TableCell>
                        <TableCell>{call.user_name}</TableCell>
                        <TableCell className="font-mono text-sm">{formatDateTime(call.created_at)}</TableCell>
                        <TableCell className="font-mono">{call.duration_minutes} min</TableCell>
                        <TableCell className="max-w-[200px] truncate">{call.notes}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <LogCallDialog open={callDialogOpen} onOpenChange={setCallDialogOpen} onSubmit={handleLogCall} clientsList={clientsList} />
      <CreateContactDialog open={contactDialogOpen} onOpenChange={setContactDialogOpen} onSubmit={handleCreateContact} clientsList={clientsList} />
      <CreateOpportunityDialog open={opportunityDialogOpen} onOpenChange={setOpportunityDialogOpen} onSubmit={handleCreateOpportunity} clientsList={clientsList} contactsList={contactsList} />
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        itemType={deleteItem.type}
        itemId={deleteItem.id}
        itemName={deleteItem.name}
        isAdmin={isAdmin}
        onDeleted={loadData}
        directDelete={async (id) => {
          if (deleteItem.type === 'contact') await handleDeleteContact(id);
          else if (deleteItem.type === 'opportunity') await handleDeleteOpportunity(id);
        }}
      />
    </div>
  );
}
